import { connect } from 'react-redux';
import OneTimeMeetingBuilder from 'components/OneTimeMeetingBuilder/OneTimeMeetingBuilder';
import { defineMessages, injectIntl } from 'react-intl';
import { FLAG_FAVORITE_ONE_TIME_MEETINGS } from 'constants/FeatureFlags';
import scheduleActions from 'actions/ScheduleModal';
import { SCHEDULE_FORM, ONE_TIME_MEETING_FORM } from 'constants/ScheduleModal';
import { START_CALL_FORM } from 'constants/StartCallModal';
import callActions from 'actions/StartCallModal';
import avActions from 'actions/AvSettingsModal';
import { cancelJoinCall } from 'actions/JoinModal';
import { SubmissionError, reset } from 'redux-form';
import contactActions from 'actions/ContactModal';
import { loadMissingMeetingInfo } from 'utils/contact-utils';
import { requireMeetingPasscodeSelector } from 'selectors/contact';
import {
  contactActions as nucleusContactActions,
  contactSelectors as nucleusContactSelectors,
  currentUserSelectors as nucleusCurrentUserSelectors,
  featureSelectors,
  contactModels,
  nucleusConstants
} from '@lifesize/nucleus';
import _get from 'lodash/get';
import _mapKeys from 'lodash/mapKeys';
import * as Logger from 'js-logger';

const nameFieldName = 'name';
const moderatorFieldName = 'moderator';
const passcodeFieldName = 'passcode';

// property = field name
// message key = error type from nucleus
const messages = {
  name: defineMessages({
    [nucleusConstants.VALIDATE_DUPLICATE]: {
      id: 'components.meetingForm.name.duplicateError',
      defaultMessage: '{value} already exists. Please choose a different name.'
    }
  }),
  timeout: {
    id: 'components.meetingForm.timeoutError',
    defaultMessage: 'Your request has timed out. Please try again.'
  },
  unknown: {
    id: 'components.meetingForm.unknownError',
    defaultMessage: 'Something went wrong. Please try again.'
  }
};

function getContact(state, userId, contactId) {
  if (contactId === userId) {
    return nucleusCurrentUserSelectors.myInfo(state);
  }

  return nucleusContactSelectors.getContactByUuidGenerator(state)(contactId) || {};
}

const mapStateToProps = (state, ownProps) => {
  const userId = _get(state, 'nucleus.userProfile.uuid');
  const meeting = ownProps.contactObj || _get(state, 'app.contactModal.contactObj') || {};
  // 1st pass, get moderator from modal form
  let moderator = _get(state, 'app.contactModal.moderator');

  if (!!moderator && !moderator.id) {
    // 2nd pass, get moderator from meeting
    getContact(state, userId, meeting.moderatorID);
  }

  if (!moderator || !moderator.id) {
    // Last pass, assign current user as moderator
    moderator = nucleusCurrentUserSelectors.myInfo(state);
  }

  const requireMeetingPasscode = requireMeetingPasscodeSelector(state);

  return {
    user: { id: userId },
    moderator,
    meeting,
    savingContact: _get(state, 'app.contacts.savingContact'),
    nameFieldName,
    existingMeetingName: _get(state, 'app.contactModal.existingName'),
    moderatorFieldName,
    contactFormError: _get(state, 'app.contactModal.contactError'),
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    initialValues: meeting,
    oneTimeFavorite: featureSelectors.getFeatureFlag(state)(FLAG_FAVORITE_ONE_TIME_MEETINGS),
    passcodeFieldName,
    requireMeetingPasscode
  };
};

function mapDispatchToProps(dispatch, ownProps) {
  const mapFormToFields = contactObj => (
    _mapKeys(contactObj, (value, key) => {
      if (key.includes(nameFieldName)) {
        return 'name';
      }

      if (key.includes(moderatorFieldName)) {
        return 'moderator';
      }

      return key;
    }));

  return {
    resetErrorState: () => dispatch(contactActions.clearContactError()),
    dispatchExistingNameError: (name) => {
      // We already know this meeting name exists from a previous submit attempt
      // Dispatch the error again so user is alerted, and submit is disabled
      const errorObj = {};
      errorObj.name =
        ownProps.intl.formatMessage(
          messages.name[nucleusConstants.VALIDATE_DUPLICATE],
          { value: name }
        );
      errorObj.existingName = name || null;
      dispatch(contactActions.contactFormError(errorObj));
    },
    handleSelectContact: (contact) => {
      if (ownProps.schedule) {
        dispatch(scheduleActions.selectContactForSchedule(contact.uuid));
        dispatch(reset(SCHEDULE_FORM));
        dispatch(scheduleActions.setScheduleFormFocus(false));
      } else {
        dispatch(callActions.selectContactForCall(contact));
        dispatch(reset(START_CALL_FORM));
        dispatch(callActions.setFormFocus(false));
      }
    },
    onBackClicked: () => {
      if (ownProps.schedule) {
        dispatch(scheduleActions.selectContactForSchedule(null));
      } else {
        dispatch(reset(START_CALL_FORM));
        dispatch(callActions.selectContactForCall(null));
        dispatch(avActions.resetCallSettings());
        dispatch(cancelJoinCall());
      }

      dispatch(contactActions.resetModerator());
      dispatch(contactActions.clearContactError());
    },
    saveContact: async (contactObj) => {
      const contact = Object.assign(new contactModels.Meeting(), mapFormToFields(contactObj));
      contact.moderatorID = _get(contact, 'moderator.id');
      contact.moderatorExtension = _get(contact, 'moderator.dialString');
      contact.meetingType = nucleusConstants.MEETING_TYPE_ONE_TIME;
      contact.tempMeeting = true;
      contact.hiddenMeeting = true;
      delete contact.moderator;
      delete contact.lecturer;
      try {
        const newContact = await dispatch(nucleusContactActions.saveContact(contact));
        // CA-1238: Load missing meeting info for one-time meetings
        loadMissingMeetingInfo(dispatch, newContact);
        dispatch(contactActions.resetModerator());
        dispatch(reset(ONE_TIME_MEETING_FORM));
        return newContact;
      } catch (error) {
        const errorObj = {};
        if (typeof error === 'object') {
          if (!!error.errorDescription) {
            // If here, the error is not in the expected context/type format (in else section below )
            switch (error.errorDescription) {
              // TODO: Created NUC pr to make this a constant:
              // @see https://github.com/lifesize/Lifesize.sky.nucleus/pull/1474
              case 'AWAIT_ACTION_TIMEOUT':
                Logger.warn('Timeout while creating meeting');
                errorObj.name = ownProps.intl.formatMessage(messages.timeout);
                break;
              case 'DisplayNameExistError':
                // Usually comes back in the context/type format, but it sometimes is on the 'errorDescription'
                errorObj.name = ownProps.intl.formatMessage(messages.name[nucleusConstants.VALIDATE_DUPLICATE],
                  { value: contact.name });
                errorObj.existingName = contact.name || null;
                break;
              default:
                Logger.warn(`Unexpected error creating meeting: ${error.errorDescription}`);
                errorObj.name = ownProps.intl.formatMessage(messages.unknown);
                break;
            }
          } else {
            error.forEach((err) => {
              errorObj[err.context.key] =
                (messages[err.context.key] && messages[err.context.key][err.type]) ?
                  ownProps.intl.formatMessage(
                    messages[err.context.key][err.type],
                    { value: err.context.value }
                  ) : err.type;
              // Save the existing name from server response for client-side validation
              errorObj.existingName = err.context.value || null;
            });
          }
          dispatch(contactActions.contactFormError(errorObj));
        }
        /* eslint-disable no-underscore-dangle */
        errorObj._error = 'Creating a contact failed';
        /* eslint-enable no-underscore-dangle */
        dispatch(reset(ONE_TIME_MEETING_FORM));
        return Promise.reject(new SubmissionError(errorObj));
      }
    }
  };
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(OneTimeMeetingBuilder));
